import { isMarried, parseNumericValue } from './helper';
import { parseDurationToMonths } from './onboardingUtils';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Month index used for date windows: January of year 0 is 0.
 * @param {number} year
 * @param {number} monthIndex 0–11
 */
function yearMonthIndex(year, monthIndex) {
  return year * 12 + monthIndex;
}

/** @param {string} value YYYY-MM-DD */
function yearMonthFromDate(value) {
  if (typeof value !== 'string') return null;
  const match = /^(\d{4})-(\d{2})-\d{2}$/.exec(value);
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return yearMonthIndex(Number(match[1]), month - 1);
}

function readNonNegative(profile, keys) {
  if (!profile || typeof profile !== 'object') return 0;
  for (const key of keys) {
    const raw = profile[key];
    if (raw == null || raw === '') continue;
    const parsed = parseNumericValue(raw, null);
    if (parsed != null && Number.isFinite(parsed)) return Math.max(0, Math.round(parsed));
  }
  return 0;
}

function lineAmount(line) {
  const parsed = parseNumericValue(line?.monthlyAmount, null);
  if (parsed == null || !Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.round(parsed);
}

/**
 * Last active month index for a windowed line.
 * The explorer stores Start Date + Duration as an inclusive endDate.
 * A numeric `duration` / `durationMonths` is used only when endDate is absent.
 */
function windowEnd(line, startIndex) {
  const end = yearMonthFromDate(line?.endDate);
  if (end != null) return end;
  const duration = Number(line?.durationMonths ?? line?.duration);
  if (Number.isFinite(duration) && duration > 0) return startIndex + Math.round(duration) - 1;
  return null;
}

function lineIsActive(line, monthIndex, { lump }) {
  const start = yearMonthFromDate(line?.startDate);
  if (start == null) return false;
  if (lump) return monthIndex === start;
  const end = windowEnd(line, start);
  if (end == null || end < start) return false;
  return monthIndex >= start && monthIndex <= end;
}

function sumLines(lines, monthIndex, { lumpField = false } = {}) {
  if (!Array.isArray(lines)) return 0;
  return lines.reduce((sum, line) => {
    const amount = lineAmount(line);
    if (amount <= 0) return sum;
    const lump = lumpField && line?.payoutType === 'lump';
    return lineIsActive(line, monthIndex, { lump }) ? sum + amount : sum;
  }, 0);
}

function launchMonthIndex(launchDate) {
  const date = launchDate instanceof Date && !Number.isNaN(launchDate.getTime())
    ? launchDate
    : new Date();
  return yearMonthIndex(date.getFullYear(), date.getMonth());
}

/**
 * Build one stacked-bar row per month of the career transition.
 *
 * N comes from v2_onboarding_ai_result.financial_profile.duration
 * (the onboarding progress object is that financial profile).
 * Month 1 is the calendar month of `launchDate` (the forecast's current month).
 *
 * Each row is TotalMonthlyIncome for that month, split into the chart series,
 * plus flat monthly expenses. net = income − expenses.
 *
 * @param {object|null} financialProfile
 * @param {object|null} incomeSources severance, unemployment, temporary, passive line items
 * @param {{ launchDate?: Date }} [options]
 * @returns {Array<{ label: string, year: string, work: number, severance: number, unemployment: number, temporary: number, passive: number, expenses: number, net: number }>}
 */
export function buildTransitionIncomeCashFlow(financialProfile, incomeSources, options = {}) {
  const profile = financialProfile && typeof financialProfile === 'object' ? financialProfile : null;
  const monthCount = profile ? parseDurationToMonths(profile) : 0;
  if (!Number.isFinite(monthCount) || monthCount <= 0) return [];

  const includeSpouse = isMarried(profile?.family_situation);
  const userWork = readNonNegative(profile, [
    'monthly_net_income_during_career_shift',
    'monthly_net_income_before_career_shift',
  ]);
  const spouseWork = includeSpouse
    ? readNonNegative(profile, ['monthly_net_income_partner'])
    : 0;
  const work = userWork + spouseWork;
  const expenses = readNonNegative(profile, ['monthly_expenses']);
  const sources = incomeSources && typeof incomeSources === 'object' ? incomeSources : {};
  const firstMonth = launchMonthIndex(options.launchDate);

  return Array.from({ length: monthCount }, (_, offset) => {
    const monthIndex = firstMonth + offset;
    const year = Math.floor(monthIndex / 12);
    const month = monthIndex % 12;
    const severance = sumLines(sources.severance, monthIndex, { lumpField: true });
    const unemployment = sumLines(sources.unemployment, monthIndex);
    const temporary = sumLines(sources.temporary, monthIndex);
    const passive = sumLines(sources.passive, monthIndex);
    const income = work + severance + unemployment + temporary + passive;
    return {
      label: MONTH_LABELS[month],
      year: String(year),
      work,
      severance,
      unemployment,
      temporary,
      passive,
      expenses,
      net: income - expenses,
    };
  });
}
